<template>
  <div class="how-page">
    <UserAppChrome back-mode="history" />

    <main class="how-shell">
      <UserSectionHeader
        eyebrow="La tua guida"
        title="Come funziona"
        description="Tutti i passaggi del percorso Fusco Fit, dall’accesso all’app fino alla consegna dei tuoi programmi."
      />

      <section class="how-hero" aria-label="Introduzione al percorso">
        <img :src="heroImage" :alt="heroImageAlt" loading="eager" />
        <div class="how-hero__shade" aria-hidden="true"></div>
        <div class="how-hero__copy">
          <h2>Il tuo percorso,<br />passo dopo passo.</h2>
          <p>Apri ogni fase per scoprire cosa fare e avere tutto a portata di mano.</p>
          <div class="how-hero__meta" aria-label="Riepilogo della guida">
            <span><strong>05</strong> passaggi</span>
            <span><strong>01</strong> app</span>
            <span><strong>01</strong> team con te</span>
          </div>
        </div>
      </section>

      <section class="steps-wrapper" aria-labelledby="steps-title">
        <header class="section-heading">
          <div>
            <p class="section-kicker">Percorso post acquisto</p>
            <h2 id="steps-title">Inizia da qui</h2>
          </div>
          <span class="section-count">5 step</span>
        </header>

        <ol class="steps-list">
          <li
            v-for="step in timelineSteps"
            :key="step.number"
            class="step-card"
            :class="{ 'step-card--open': isStepOpen(step.number) }"
          >
            <button
              :id="`step-trigger-${step.number}`"
              class="step-trigger"
              type="button"
              :aria-expanded="isStepOpen(step.number)"
              :aria-controls="`step-panel-${step.number}`"
              @click="toggleStep(step.number)"
            >
              <span class="step-index">{{ String(step.number).padStart(2, '0') }}</span>
              <span class="step-texts">
                <strong class="step-title">{{ step.title }}</strong>
                <span class="step-summary">{{ step.headline || step.description }}</span>
              </span>
              <span class="step-toggle-icon material-icons" aria-hidden="true">
                {{ isStepOpen(step.number) ? 'remove' : 'add' }}
              </span>
            </button>

            <transition name="accordion">
              <div
                v-show="isStepOpen(step.number)"
                :id="`step-panel-${step.number}`"
                class="step-panel"
                role="region"
                :aria-labelledby="`step-trigger-${step.number}`"
              >
                <p v-if="step.description" class="step-description">{{ step.description }}</p>

                <p v-if="step.highlight" class="step-highlight">
                  <span class="material-icons" aria-hidden="true">priority_high</span>
                  <span>{{ step.highlight }}</span>
                </p>

                <ul v-if="step.bullets?.length" class="plain-list">
                  <li v-for="bullet in step.bullets" :key="bullet">
                    <span class="material-icons" aria-hidden="true">check</span>
                    <span>{{ bullet }}</span>
                  </li>
                </ul>

                <div v-if="step.apps?.length" class="app-downloads">
                  <article v-for="appId in step.apps" :key="appId" class="app-card">
                    <div class="app-card__head">
                      <div class="app-icon-slot">
                        <img
                          v-if="appsCatalog[appId].logo"
                          :src="appsCatalog[appId].logo"
                          :alt="appsCatalog[appId].name"
                          loading="lazy"
                        />
                        <span v-else class="material-icons" aria-hidden="true">
                          {{ appsCatalog[appId].icon }}
                        </span>
                      </div>
                      <div>
                        <p class="app-name">{{ appsCatalog[appId].name }}</p>
                        <p class="app-desc">{{ appsCatalog[appId].description }}</p>
                      </div>
                    </div>

                    <div class="cta-buttons">
                      <a
                        v-for="link in appsCatalog[appId].links"
                        :key="link.label"
                        class="cta-btn"
                        :href="link.url || '#'"
                        target="_blank"
                        rel="noopener noreferrer"
                        :aria-disabled="!link.url"
                      >
                        <span>{{ link.label }}</span>
                        <span class="material-icons" aria-hidden="true">north_east</span>
                      </a>
                    </div>
                  </article>
                </div>

                <div v-if="step.tutorials?.length" class="tutorial-links">
                  <p class="tutorial-title">
                    <span class="material-icons" aria-hidden="true">play_circle</span>
                    Video tutorial
                  </p>
                  <ul>
                    <li v-for="tutorial in step.tutorials" :key="tutorial.label">
                      <a :href="tutorial.url" target="_blank" rel="noopener noreferrer">
                        <span>{{ tutorial.label }}</span>
                        <span class="material-icons" aria-hidden="true">play_arrow</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </li>
        </ol>
      </section>

      <section class="notes-section" aria-labelledby="notes-title">
        <header class="section-heading section-heading--notes">
          <div>
            <p class="section-kicker">Da ricordare</p>
            <h2 id="notes-title">Informazioni utili</h2>
          </div>
        </header>

        <div class="note-grid">
          <article class="note-card note-card--delivery">
            <span class="note-card__icon material-icons" aria-hidden="true">rocket_launch</span>
            <span class="note-card__number" aria-hidden="true">01</span>
            <h3>Dopo la consegna</h3>
            <p>Potrai iniziare subito a seguire il tuo percorso.</p>
            <p>
              Per dubbi o chiarimenti, scrivici illimitatamente su “Assistenza tecnica” nell’app.
              Per domande alimentari hai 2 ticket al mese su “Assistenza nutrizionale”.
            </p>
          </article>

          <article class="note-card note-card--calls">
            <span class="note-card__icon material-icons" aria-hidden="true">video_call</span>
            <span class="note-card__number" aria-hidden="true">02</span>
            <h3>Call tecniche</h3>
            <p>
              La call tecnica può essere utilizzata solo all’interno del programma in corso. Se hai
              acquistato 6 programmi, avrai diritto a 1 call per ogni programma.
            </p>
            <p>
              Al termine del singolo programma la call associata scade, se non utilizzata. Prima della
              videocall annota ogni dubbio sulla scheda: lo analizzeremo insieme.
            </p>
          </article>

          <article class="note-card note-card--schedule">
            <span class="note-card__icon material-icons" aria-hidden="true">schedule</span>
            <span class="note-card__number" aria-hidden="true">03</span>
            <h3>Orari assistenza</h3>
            <ul class="schedule">
              <li><span>Lunedì – Venerdì</span><strong>9:00 — 19:00</strong></li>
              <li><span>Sabato</span><strong>10:00 — 13:00</strong></li>
            </ul>
            <nav class="service-links" aria-label="Documenti servizio">
              <a href="https://www.fusco.fit/termini-e-condizioni/" target="_blank" rel="noopener noreferrer">
                <span>Termini e condizioni</span>
                <span class="material-icons" aria-hidden="true">north_east</span>
              </a>
              <a href="https://www.fusco.fit/scheda-servizi/" target="_blank" rel="noopener noreferrer">
                <span>Scheda servizi</span>
                <span class="material-icons" aria-hidden="true">north_east</span>
              </a>
            </nav>
          </article>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { ref } from 'vue';
import heroBanner from '@/assets/banner.png';
import connectAppIcon from '@/assets/fusco-fit-connect-app-icon.jpg';
import UserAppChrome from './UserAppChrome.vue';
import UserSectionHeader from './UserSectionHeader.vue';

export default {
  name: 'ComeFunzionaMobile',
  components: {
    UserAppChrome,
    UserSectionHeader,
  },
  setup() {
    const heroImage = heroBanner;
    const heroImageAlt = 'Banner Fusco Fit';

    const appsCatalog = {
      connect: {
        name: 'Fusco Fit Connect (logo nero)',
        description: 'Un’unica app per comunicazioni, assistenza, alimentazione e allenamenti',
        icon: 'chat',
        logo: connectAppIcon,
        links: [
          {
            label: 'iOS – Fusco Fit Connect',
            url: 'https://apps.apple.com/it/app/fusco-fit/id6444216077'
          },
          {
            label: 'Android – Fusco Fit Connect',
            url: 'https://play.google.com/store/apps/details?id=com.elev8ted.fuscofit'
          }
        ]
      }
    };

    const timelineSteps = [
      {
        number: 1,
        title: 'Accedi all’app',
        headline: 'Scarica la nostra app.',
        description:
          'Fusco Fit Connect (logo nero) è l’unica app che ti serve: qui trovi comunicazioni, assistenza, piano alimentare e allenamenti.',
        highlight:
          'Dopo il primo accesso apri subito un ticket in Assistenza tecnica: iniziamo a seguirti da lì.',
        bullets: [
          'Cerca “Fusco Fit Connect” nello store del tuo telefono: riconoscila dal logo nero.',
          'Registra l’account con l’email usata all’acquisto.',
          'Accedi una sola volta per gestire tutto il tuo percorso.'
        ],
        apps: ['connect'],
        tutorials: [
          {
            label: 'Registrazione e accesso',
            url: 'https://www.metodofusco.it/media/accesso_applicazione_v1%20%28720p%29.mp4'
          },
          {
            label: 'Apertura ticket assistenza tecnica',
            url: 'https://www.metodofusco.it/media/apertura_assistenza_tecnica_v1%20%28720p%29.mp4'
          }
        ]
      },
      {
        number: 2,
        title: 'Compila il questionario',
        headline: 'Risposte complete = percorso personalizzato',
        description:
          'Riceverai il link via email. Compilarlo è indispensabile per personalizzare il percorso.',
        bullets: [
          'Controlla anche le cartelle spam/promozioni.',
          'Inserisci più dettagli possibili, soprattutto nelle note libere.',
          'Hai un appuntamento in studio? Compilalo con noi sul posto per evitare doppioni.'
        ]
      },
      {
        number: 3,
        title: 'Video call con la nutrizionista',
        headline: 'Confronto diretto con il team',
        description:
          'Appena il questionario è approvato ti contattiamo per fissare la videocall. Se perdi la chiamata, richiamaci quando puoi.',
        bullets: [
          'La sessione serve a rifinire alimentazione e allenamento.',
          'Prepara domande e note così sfruttiamo al massimo il tempo insieme.'
        ]
      },
      {
        number: 4,
        title: 'Call tecnica (servizio Plus)',
        headline: 'Richiedi la videocall dall’app',
        description:
          'Se il tuo pacchetto la include, riceverai un messaggio direttamente nell’app per prenotarla.',
        bullets: [
          'Ti chiediamo le fasce orarie disponibili dopo la consegna delle programmazioni.',
          'La call è legata al programma in corso: se termina, anche la call scade.',
          'Annota dubbi e richieste sulla scheda così li affrontiamo in diretta.'
        ]
      },
      {
        number: 5,
        title: 'Consegna e supporto',
        headline: 'Ricevi notifiche e inizi subito',
        description:
          'Entro due giorni lavorativi dalla call con la nutrizionista riceverai programmi e notifiche.',
        bullets: [
          'Il piano alimentare è disponibile nella sezione Alimentazione.',
          'Il programma di allenamento è disponibile nella sezione Allenamenti.',
          'Tutto il percorso è raccolto in questa unica app.',
          'Dopo la consegna puoi scriverci sempre su Assistenza tecnica e usare i ticket nutrizionali previsti.'
        ]
      }
    ];

    const activeStep = ref(timelineSteps[0]?.number ?? null);

    const toggleStep = (stepNumber) => {
      activeStep.value = activeStep.value === stepNumber ? null : stepNumber;
    };

    const isStepOpen = (stepNumber) => activeStep.value === stepNumber;

    return {
      heroImage,
      heroImageAlt,
      appsCatalog,
      timelineSteps,
      toggleStep,
      isStepOpen
    };
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:ital,wght@0,700;0,800;0,900;1,800;1,900&display=swap');

.material-icons {
  color: white;
}
.white-text {
  color: white;
}


:global(body) {
  background: #050505;
  color: #f5f5f5;
}

.how-it-works {
  padding: 1.5rem 0 3rem;
  background: #050505;
}

.how-card {
  border-radius: 34px;
  overflow: hidden;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: radial-gradient(circle at top, rgba(32, 32, 32, 0.45), #050505);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.7);
}

.top-bar {
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(5, 5, 5, 0.85);
}


.top-bar .icon-link:focus-visible {
  outline: 2px solid #f7d047;
  outline-offset: 2px;
}

.brand {
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #f6f6f6;
}

.how-body {
  padding-top: 17px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.hero-card {
  border-radius: 28px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  background: #0b0b0b;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.hero-media {
  height: 220px;
  overflow: hidden;
}

.hero-media img {
  width: 100%;
  height: 110%;
  object-fit: cover;
}

.hero-copy {
  padding: 1.6rem;
  background: linear-gradient(145deg, rgba(20, 20, 20, 0.95), rgba(8, 8, 8, 0.95));
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.hero-copy h1 {
  margin: 0;
  font-size: 1.7rem;
  color: #ffffff;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.78rem;
  color: #a4a4a4;
  margin: 0;
}

.eyebrow-light {
  color: #c1c1c1;
}

.lead {
  font-size: 1rem;
  color: #eeeeee;
  margin: 0;
}

.muted {
  color: #9a9a9a;
  margin: 0;
}

.steps-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.steps-title h2 {
  font-size: 1.3rem;
  margin: 0.15rem 0 0;
  color: #f3f3f3;
}

.steps-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.step-card {
  border-radius: 24px;
  background: rgba(12, 12, 12, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.04);
  overflow: hidden;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.step-card--open {
  border-color: #f7d047;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.55);
}

.step-trigger {
  width: 100%;
  border: none;
  background: none;
  color: inherit;
  padding: 1.15rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  text-align: left;
}

.step-trigger:focus-visible {
  outline: 2px solid #f7d047;
  outline-offset: -4px;
}

.step-labels {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.step-index {
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: #f4f4f4;
}

.step-card--open .step-index {
  background: #f7d047;
  color: #050505;
}

.step-texts {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.step-title {
  margin: 0;
  font-weight: 600;
  font-size: 1rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.step-summary {
  margin: 0;
  color: #9e9e9e;
  font-size: 0.9rem;
}

.step-toggle-icon {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: #f7d047;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #050505;
  border: 1px solid transparent;
  transition: transform 0.25s ease;
}

.step-card--open .step-toggle-icon {
  transform: rotate(180deg);
}

.step-panel {
  padding: 0 1.25rem 1.2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.step-description {
  margin: 0;
  color: #b1b1b1;
  line-height: 1.5;
}

.step-highlight {
  padding: 0.75rem 1rem;
  border-radius: 14px;
  background: rgba(247, 208, 71, 0.15);
  color: #fff8d6;
  margin: 0;
  font-weight: 600;
}

.plain-list {
  margin: 0;
  padding-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  color: #d2d2d2;
}

.app-downloads {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.app-card {
  border-radius: 18px;
  padding: 1rem;
  background: rgba(18, 18, 18, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.04);
  text-align: center;
}

.app-icon-slot {
  width: 66px;
  height: 66px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #050505;
  margin: 0 auto 0.5rem;
}

.app-icon-slot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 18px;
}

.app-name {
  margin: 0;
  font-weight: 600;
  color: #f8f8f8;
}

.app-desc {
  margin: 0.1rem 0 0.45rem;
  font-size: 0.85rem;
  color: #b4b4b4;
}

.cta-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.1rem;
  border-radius: 999px;
  background: linear-gradient(135deg, #f7d047, #f4b923);
  color: #050505;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  min-width: 140px;
}

.cta-btn[aria-disabled='true'] {
  background: #3a3a3a;
  color: #959595;
  pointer-events: none;
}

.tutorial-links {
  padding: 0.8rem 1rem;
  border-radius: 14px;
  background: rgba(60, 60, 60, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.tutorial-title {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #d0d0d0;
  margin: 0 0 0.4rem;
}

.tutorial-links ul {
  margin: 0;
  padding-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.tutorial-links a {
  color: #d4d4d4;
  text-decoration: underline;
  font-weight: 500;
}

.note-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.note-card {
  background: rgba(20, 20, 20, 0.9);
  border-radius: 20px;
  padding: 1.2rem 1.3rem;
  color: #c5c5c5;
  line-height: 1.5;
  border: 1px solid rgba(255, 255, 255, 0.04);
  box-shadow: 0 18px 30px rgba(0, 0, 0, 0.35);
}

.note-card h3 {
  margin: 0 0 0.4rem;
  color: #f7f7f7;
}

.note-title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
}

.schedule {
  margin: 0.6rem 0 0;
  padding-left: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.service-links {
  margin-top: 1rem;
  padding-top: 0.9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.service-links a {
  color: #f7d047;
  font-weight: 600;
  text-decoration: none;
}

.service-links a:focus-visible {
  outline: 2px solid #f7d047;
  outline-offset: 3px;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.25s ease, opacity 0.25s ease;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 580px;
  opacity: 1;
}

@media (min-width: 600px) {
  .hero-card {
    grid-template-columns: 1fr 1fr;
  }

  .hero-media {
    height: 100%;
  }
}

@media (min-width: 768px) {
  .how-card {
    max-width: 520px;
    margin: 0 auto;
  }
}

/* Fusco Fit user app redesign */
:global(body) {
  background: #f3f1ed;
  color: #111110;
}

.how-page,
.how-page *,
.how-page *::before,
.how-page *::after {
  box-sizing: border-box;
}

.how-page {
  --ff-black: #111110;
  --ff-red: #d50b35;
  --ff-red-dark: #a90f29;
  --ff-white: #ffffff;
  --ff-paper: #f3f1ed;
  --ff-muted: #65635f;
  --ff-border: rgba(17, 17, 16, 0.09);
  min-height: 100vh;
  overflow-x: hidden;
  background:
    radial-gradient(circle at 87% 4%, rgba(213, 11, 53, 0.09), transparent 25rem),
    radial-gradient(circle at 8% 63%, rgba(255, 255, 255, 0.92), transparent 34rem),
    linear-gradient(145deg, #f8f7f4 0%, #efede8 100%);
  color: var(--ff-black);
  font-family: 'DM Sans', sans-serif;
}

.how-shell {
  width: min(calc(100% - 48px), 1392px);
  margin: 0 auto;
  padding: clamp(34px, 5vw, 68px) 0 150px;
}

.how-hero {
  position: relative;
  min-height: clamp(360px, 46vw, 560px);
  margin-bottom: clamp(56px, 8vw, 92px);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.46);
  border-radius: 30px;
  background: var(--ff-black);
  box-shadow: 0 30px 70px rgba(17, 17, 16, 0.16);
  isolation: isolate;
}

.how-hero::after {
  position: absolute;
  z-index: 1;
  inset: auto 0 0;
  height: 4px;
  background: linear-gradient(90deg, var(--ff-red), #f03458 62%, rgba(240, 52, 88, 0));
  content: '';
}

.how-hero > img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 45%;
  filter: saturate(0.82) contrast(1.02);
  transform: scale(1.01);
}

.how-hero__shade {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(7, 8, 8, 0.94) 0%, rgba(7, 8, 8, 0.79) 38%, rgba(7, 8, 8, 0.2) 78%),
    linear-gradient(0deg, rgba(5, 5, 5, 0.76) 0%, transparent 55%);
}

.how-hero__copy {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: min(62%, 720px);
  min-height: inherit;
  padding: clamp(30px, 5vw, 72px);
  color: var(--ff-white);
  text-align: center;
}

.how-hero__badge {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 8px;
  margin-bottom: 18px;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.how-hero__badge .material-icons {
  color: var(--ff-white);
  font-size: 17px;
}

.how-hero__copy h2 {
  max-width: 670px;
  margin: 0;
  color: var(--ff-white);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(2.25rem, 5.5vw, 5rem);
  font-style: italic;
  font-weight: 900;
  line-height: 0.91;
  letter-spacing: -0.065em;
  text-transform: uppercase;
}

.how-hero__copy > p {
  max-width: 560px;
  margin: 22px 0 0;
  color: rgba(255, 255, 255, 0.76);
  font-size: clamp(0.96rem, 1.6vw, 1.1rem);
  line-height: 1.5;
  text-align: center;
}

.how-hero__meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 26px;
}

.how-hero__meta span {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 9px 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.74rem;
  font-weight: 600;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.how-hero__meta strong {
  color: var(--ff-white);
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  font-weight: 900;
}

.steps-wrapper,
.notes-section {
  display: block;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 24px;
}

.section-kicker {
  margin: 0 0 7px;
  color: var(--ff-red);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(2rem, 4.3vw, 3.5rem);
  font-weight: 900;
  line-height: 0.96;
  letter-spacing: -0.055em;
  text-transform: uppercase;
}

.section-count {
  flex: 0 0 auto;
  padding: 8px 12px;
  border: 1px solid rgba(213, 11, 53, 0.16);
  border-radius: 999px;
  background: rgba(213, 11, 53, 0.07);
  color: var(--ff-red);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.steps-list {
  display: grid;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.step-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.68);
  box-shadow:
    0 14px 38px rgba(17, 17, 16, 0.055),
    inset 0 1px 0 rgba(255, 255, 255, 0.94);
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
  backdrop-filter: blur(24px) saturate(1.3);
  -webkit-backdrop-filter: blur(24px) saturate(1.3);
}

.step-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: var(--ff-red);
  content: '';
  opacity: 0;
  transition: opacity 0.25s ease;
}

.step-card--open {
  border-color: rgba(213, 11, 53, 0.26);
  background: rgba(255, 255, 255, 0.84);
  box-shadow:
    0 22px 55px rgba(17, 17, 16, 0.085),
    inset 0 1px 0 #fff;
}

.step-card--open::before {
  opacity: 1;
}

.step-trigger {
  display: grid;
  grid-template-columns: 62px minmax(0, 1fr) 42px;
  align-items: center;
  gap: 18px;
  width: 100%;
  padding: 18px 20px;
  border: 0;
  background: transparent;
  color: var(--ff-black);
  text-align: left;
  cursor: pointer;
}

.step-trigger:focus-visible,
.cta-btn:focus-visible,
.tutorial-links a:focus-visible,
.service-links a:focus-visible {
  outline: 3px solid rgba(213, 11, 53, 0.34);
  outline-offset: 3px;
}

.step-index {
  display: grid;
  place-items: center;
  width: 62px;
  height: 62px;
  border: 1px solid rgba(213, 11, 53, 0.16);
  border-radius: 17px;
  background: linear-gradient(145deg, rgba(213, 11, 53, 0.12), rgba(213, 11, 53, 0.035));
  color: var(--ff-red);
  font-family: 'Montserrat', sans-serif;
  font-size: 1.05rem;
  font-weight: 900;
  letter-spacing: -0.03em;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition: background 0.25s ease, color 0.25s ease;
}

.step-card--open .step-index {
  border-color: var(--ff-red);
  background: var(--ff-red);
  color: var(--ff-white);
}

.step-texts {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
}

.step-title {
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1rem, 2vw, 1.18rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.025em;
}

.step-summary {
  overflow: hidden;
  color: var(--ff-muted);
  font-size: 0.89rem;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.step-toggle-icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: var(--ff-black);
  color: var(--ff-white);
  font-size: 21px;
  box-shadow: 0 8px 18px rgba(17, 17, 16, 0.14);
}

.step-card--open .step-toggle-icon {
  background: var(--ff-red);
  transform: none;
}

.step-panel {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin: 0 20px;
  padding: 24px 0 26px 80px;
  border-top: 1px solid var(--ff-border);
}

.step-description {
  max-width: 920px;
  margin: 0;
  color: #3f3e3a;
  font-size: 1rem;
  line-height: 1.6;
}

.step-highlight {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  max-width: 920px;
  margin: 0;
  padding: 15px 16px;
  border: 1px solid rgba(213, 11, 53, 0.16);
  border-radius: 15px;
  background: rgba(213, 11, 53, 0.07);
  color: #5d1223;
  font-weight: 700;
  line-height: 1.48;
}

.step-highlight .material-icons {
  flex: 0 0 auto;
  color: var(--ff-red);
  font-size: 21px;
}

.plain-list {
  display: grid;
  gap: 10px;
  max-width: 920px;
  margin: 0;
  padding: 0;
  color: #4c4a46;
  list-style: none;
}

.plain-list li {
  display: grid;
  grid-template-columns: 23px minmax(0, 1fr);
  gap: 9px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #4c4a46 !important;
  line-height: 1.48;
}

.how-page .step-panel .plain-list li > span:last-child {
  border: 0;
  color: #4c4a46 !important;
}

.plain-list .material-icons {
  display: grid;
  place-items: center;
  width: 21px;
  height: 21px;
  margin-top: 1px;
  border-radius: 50%;
  background: rgba(213, 11, 53, 0.1);
  color: var(--ff-red);
  font-size: 15px;
  font-weight: 700;
}

.app-downloads {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.app-card {
  padding: 17px;
  border: 1px solid var(--ff-border);
  border-radius: 19px;
  background: rgba(246, 245, 242, 0.82);
  text-align: left;
}

.app-card__head {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
}

.app-icon-slot {
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  margin: 0;
  overflow: hidden;
  border: 1px solid rgba(17, 17, 16, 0.07);
  border-radius: 14px;
  background: var(--ff-white);
  box-shadow: 0 8px 18px rgba(17, 17, 16, 0.08);
}

.app-icon-slot img {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}

.app-name {
  margin: 0;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1.2;
}

.app-desc {
  margin: 4px 0 0;
  color: var(--ff-muted);
  font-size: 0.78rem;
  line-height: 1.35;
}

.cta-buttons {
  display: grid;
  gap: 7px;
  margin-top: 14px;
}

.cta-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 11px;
  background: var(--ff-white);
  color: var(--ff-black);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.25;
  text-decoration: none;
}

.cta-btn:first-child {
  border-color: transparent;
  background: var(--ff-black);
  color: var(--ff-white);
}

.cta-btn .material-icons {
  flex: 0 0 auto;
  color: currentColor;
  font-size: 17px;
}

.tutorial-links {
  padding: 16px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 17px;
  background: var(--ff-black);
  color: var(--ff-white);
}

.tutorial-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 11px;
  color: rgba(255, 255, 255, 0.64);
  font-size: 0.69rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.tutorial-title .material-icons {
  color: var(--ff-red);
  font-size: 19px;
}

.tutorial-links ul {
  display: grid;
  gap: 7px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tutorial-links a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 11px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: var(--ff-white);
  font-size: 0.84rem;
  font-weight: 600;
  text-decoration: none;
}

.tutorial-links a .material-icons {
  color: var(--ff-white);
  font-size: 19px;
}

.notes-section {
  margin-top: clamp(64px, 9vw, 110px);
}

.section-heading--notes {
  margin-bottom: 24px;
}

.note-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.note-card {
  position: relative;
  min-height: 310px;
  overflow: hidden;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.84);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.69);
  color: #4a4844;
  line-height: 1.52;
  box-shadow:
    0 18px 44px rgba(17, 17, 16, 0.06),
    inset 0 1px 0 rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(22px) saturate(1.25);
  -webkit-backdrop-filter: blur(22px) saturate(1.25);
}

.note-card::after {
  position: absolute;
  inset: 0 18px auto;
  height: 3px;
  border-radius: 0 0 999px 999px;
  background: linear-gradient(90deg, transparent, var(--ff-red) 22%, var(--ff-red) 78%, transparent);
  content: '';
}

.note-card__icon {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin-bottom: 42px;
  border: 1px solid rgba(213, 11, 53, 0.16);
  border-radius: 15px;
  background: rgba(213, 11, 53, 0.08);
  color: var(--ff-red);
  font-size: 23px;
}

.note-card__number {
  position: absolute;
  top: 23px;
  right: 22px;
  color: rgba(17, 17, 16, 0.045);
  font-family: 'Montserrat', sans-serif;
  font-size: 3.5rem;
  font-style: italic;
  font-weight: 900;
  line-height: 0.8;
  letter-spacing: -0.09em;
}

.note-card h3 {
  margin: 0 0 12px;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: 1.18rem;
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.035em;
}

.note-card p {
  margin: 0 0 10px;
  font-size: 0.9rem;
}

.note-card p:last-child {
  margin-bottom: 0;
}

.schedule {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.schedule li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--ff-border);
  font-size: 0.82rem;
}

.note-card .schedule li > span {
  color: var(--ff-muted) !important;
}

.schedule strong {
  color: var(--ff-black) !important;
}

.service-links {
  display: grid;
  gap: 6px;
  margin-top: 14px;
  padding-top: 0;
  border-top: 0;
}

.service-links a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 9px 11px;
  border-radius: 10px;
  background: rgba(17, 17, 16, 0.055);
  color: var(--ff-black);
  font-size: 0.8rem;
  font-weight: 700;
  text-decoration: none;
}

.service-links .material-icons {
  color: var(--ff-red);
  font-size: 17px;
}

.accordion-enter-active,
.accordion-leave-active {
  overflow: hidden;
  transition: max-height 0.32s ease, opacity 0.24s ease;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 1400px;
  opacity: 1;
}

@media (max-width: 900px) {
  .how-hero__copy {
    width: 72%;
  }

  .note-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .note-card--schedule {
    grid-column: 1 / -1;
    min-height: 0;
  }
}

@media (max-width: 620px) {
  .how-shell {
    width: calc(100% - 24px);
    padding: 28px 0 132px;
  }

  .how-hero {
    min-height: 420px;
    margin-bottom: 54px;
    border-radius: 24px;
  }

  .how-hero > img {
    object-position: 48% center;
  }

  .how-hero__shade {
    background:
      linear-gradient(0deg, rgba(7, 8, 8, 0.96) 0%, rgba(7, 8, 8, 0.72) 48%, rgba(7, 8, 8, 0.13) 100%),
      linear-gradient(90deg, rgba(7, 8, 8, 0.25), transparent 75%);
  }

  .how-hero__copy {
    justify-content: flex-end;
    width: 100%;
    padding: 24px 22px 26px;
  }

  .how-hero__badge {
    margin-bottom: 13px;
  }

  .how-hero__copy h2 {
    max-width: 330px;
    font-size: clamp(2rem, 10.8vw, 2.75rem);
  }

  .how-hero__copy > p {
    max-width: 330px;
    margin-top: 14px;
    font-size: 0.9rem;
  }

  .how-hero__meta {
    gap: 6px;
    margin-top: 17px;
  }

  .how-hero__meta span {
    padding: 7px 9px;
    font-size: 0.6875rem;
  }

  .section-heading {
    margin: 0 6px 18px;
  }

  .section-heading h2 {
    font-size: 2rem;
  }

  .step-card {
    border-radius: 19px;
  }

  .step-trigger {
    grid-template-columns: 50px minmax(0, 1fr) 36px;
    gap: 11px;
    min-height: 88px;
    padding: 14px;
  }

  .step-index {
    width: 50px;
    height: 50px;
    border-radius: 14px;
    font-size: 0.9rem;
  }

  .step-title {
    font-size: 0.93rem;
  }

  .step-summary {
    font-size: 0.76rem;
  }

  .step-toggle-icon {
    width: 36px;
    height: 36px;
    font-size: 19px;
  }

  .step-panel {
    gap: 15px;
    margin: 0 14px;
    padding: 19px 0 21px;
  }

  .step-description,
  .step-highlight,
  .plain-list {
    font-size: 0.88rem;
  }

  .app-downloads {
    grid-template-columns: 1fr;
  }

  .app-card {
    padding: 14px;
  }

  .notes-section {
    margin-top: 64px;
  }

  .note-grid {
    grid-template-columns: 1fr;
  }

  .note-card,
  .note-card--schedule {
    grid-column: auto;
    min-height: 0;
    padding: 20px;
    border-radius: 20px;
  }

  .note-card__icon {
    margin-bottom: 32px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-card,
  .step-card::before,
  .step-index,
  .accordion-enter-active,
  .accordion-leave-active {
    transition: none;
  }
}
</style>
